import type { AppRoutes } from "@for-it/web-lib";
import { Permission } from "app-domain";
import CompanyLayout from "./layouts/company-layout.js";
import HomeLayout from "./layouts/home.js";
import HomeLayoutWithoutSidebar from "./layouts/layout-without-sidebar.js";
/*
 *  In this file, we define all the routes that the frontend will use.
 *  We do this for a few reasons:
 *      - To have a single source of truth for all the routes in the frontend. This allows us to easily change the routes in the future without braking any links in the application
 *     - To configure permissions for each route. This allows us to easily restrict access to certain routes based on the current user's permissions
 *      - To only enable pages that are defined in the routes. This allows us to easily disable pages that are not yet implemented or pages that are not yet ready to be shown to the user
 *
 *  Note 1: that we do not define the actual components that are shown on the routes here. We only define the layout that is shown on the route, and the pages are automatically loaded based on the path in the pages directory. This means that "/login", por example, will automatically load the component from "pages/login.tsx". If there is no page for a route, the app will not start and will throw an error.
 *  Note 2: Some layouts are loaded as dynamic imports (same as the routes) to reduce the bundle size. This means that the layout is only loaded when the route is accessed, and not when the app is loaded.
 *  Note 3: The paths are defined as functions that return the path. This is done so we can call these paths with parameters (e.g./user/:id) and the path will be correctly generated. This is useful for generating links in the application (e.g. <Link to={routes.USER.path({ id: 1 })}>User 1</Link>. On the other hand, if we don't pass any parameters, the path will continue to be the template string (e.g. /user/:id).
 */

// ======== LAZY LOADED LAYOUTS ========

// ======== ROUTES ========
export const Routes = {
    HOME: {
        path: () => "/",
        layout: HomeLayoutWithoutSidebar,
        isAuthRequired: false,
    },
    LOGIN: {
        path: () => "/login",
        layout: HomeLayoutWithoutSidebar,
        isAuthRequired: false,
    },
    REGISTER: {
        path: () => "/register",
        layout: HomeLayoutWithoutSidebar,
        isAuthRequired: false,
    },
    COMPLETE_PROFILE: {
        path: (id?: string) => `/applicant/${id ?? "[id]"}/complete-profile`,
        layout: HomeLayoutWithoutSidebar,
        isAuthRequired: false,
    },
    PROFILE_APPLICANT: {
        path: (id?: string) => `/applicant/${id ?? "[id]"}/profile`,
        layout: HomeLayout,
        isAuthRequired: true,
        requiredPermissions: [Permission.GET_APPLICANT_DETAILS],
    },
    COMPLETE_PROFILE_JOB_DETAILS: {
        path: (id?: string) => `/applicant/${id ?? "[id]"}/job-details`,
        layout: HomeLayoutWithoutSidebar,
        isAuthRequired: false,
    },
    RESET_PASSWORD: {
        path: () => "/reset-password",
        layout: HomeLayout,
        isAuthRequired: false,
    },
    ADMIN_COMPANY_TABLE: {
        path: () => "/admin/company-table",
        requiredPermissions: [Permission.VIEW_ADMIN_DASHBOARD],
        layout: HomeLayout,
    },
    COMPLETE_PROFILE_COMPANY: {
        path: (id?: string) => `/company/${id ?? "[id]"}/complete-profile`,
        layout: HomeLayoutWithoutSidebar,
        isAuthRequired: false,
    },
    COMPLETE_PROFILE_COMPANY_DETAILS: {
        path: (id?: string) => `/company/${id ?? "[id]"}/profile-details`,
        layout: HomeLayoutWithoutSidebar,
    },
    PROFILE_COMPANY: {
        path: (id?: string) => `/company/${id ?? "[id]"}/profile-company`,
        layout: HomeLayout,
        isAuthRequired: true,
        requiredPermissions: [Permission.GET_COMPANY_DETAILS],
    },
    APPLICANTS_TABLE: {
        path: () => "/admin/applicant-table",
        layout: HomeLayout,
        isAuthRequired: true,
        requiredPermissions: [],
    },
    COMPANY_TABLE: {
        path: () => "/admin/company-table",
        layout: HomeLayout,
        isAuthRequired: true,
        requiredPermissions: [Permission.VIEW_ADMIN_DASHBOARD],
    },
    CREATE_PUBLICATION: {
        path: () => "/company/create-publication",
        layout: HomeLayout,
        isAuthRequired: true,
        requiredPermissions: [Permission.CREATE_PUBLICATION],
    },
    EDIT_PUBLICATION: {
        path: (id?: string) =>
            `/company/publications/${id ?? "[id]"}/edit-publication`,
        layout: HomeLayout,
        isAuthRequired: true,
        requiredPermissions: [Permission.UPDATE_PUBLICATION],
    },
    COMPANY_PUBLICATIONS: {
        path: () => "/company/publications",
        layout: HomeLayout,
        isAuthRequired: true,
        requiredPermissions: [],
    },
    APPLICANT_PUBLICATIONS: {
        path: () => "/applicant/publications",
        layout: HomeLayout,
        isAuthRequired: true,
        requiredPermissions: [],
    },
    ADMIN_PUBLICATIONS: {
        path: () => "/admin/publications",
        layout: HomeLayout,
        isAuthRequired: true,
        requiredPermissions: [Permission.VIEW_ADMIN_DASHBOARD],
    },
    PUBLICATION_DETAIL: {
        path: (id?: string) => `/publication/${id ?? "[id]"}/detail`,
        layout: HomeLayout,
        isAuthRequired: true,
        requiredPermissions: [],
    },
    COMING_SOON: {
        path: () => "/coming-soon",
        layout: HomeLayout,
        isAuthRequired: false,
    },
    ADMINISTRATORS: {
        path: () => "/admin/administrators",
        layout: HomeLayout,
        isAuthRequired: true,
        requiredPermissions: [Permission.VIEW_ADMIN_DASHBOARD],
    },
    APPLICANT_PROFILE: {
        path: () => "/admin/applicant-profile",
        layout: HomeLayout,
        isAuthRequired: true,
        requiredPermissions: [Permission.VIEW_ADMIN_DASHBOARD],
    },
    APPLICANTS: {
        path: () => "/company/applicants",
        layout: HomeLayout,
        isAuthRequired: true,
        requiredPermissions: [],
    },
    APPLICATION: {
        path: (id?: string) =>
            `/company/publications/${id ?? "[id]"}/applications`,
        layout: HomeLayout,
        isAuthRequired: true,
        requiredPermissions: [],
    },
    APPLICATIONS: {
        path: () => "/applicant/applications",
        layout: HomeLayout,
        isAuthRequired: true,
        requiredPermissions: [],
    },
    NOTIFICATIONS: {
        path: () => "/applicant/notifications",
        layout: HomeLayout,
        isAuthRequired: true,
        requiredPermissions: [],
    },
    ERROR_PAGE: {
        path: () => "/error-page",
        layout: CompanyLayout,
    },
    CHANGE_PASSWORD: {
        path: () => "/change-password",
        layout: HomeLayout,
        isAuthRequired: true,
    },
    CREATE_CERTIFICATION: {
        path: () => "/admin/create-certification",
        layout: HomeLayout,
        isAuthRequired: true,
        requiredPermissions: [Permission.VIEW_ADMIN_DASHBOARD],
    },
    CERTIFICATION_TABLE: {
        path: () => "/admin/certification-table",
        layout: HomeLayout,
        isAuthRequired: true,
        requiredPermissions: [Permission.VIEW_ADMIN_DASHBOARD],
    },
    EDIT_CERTIFICATION: {
        path: (id?: string) => `/admin/${id ?? "[id]"}/edit-certification`,
        layout: HomeLayout,
        isAuthRequired: true,
        requiredPermissions: [Permission.EDIT_CERTIFICATION],
    },
} as const satisfies AppRoutes<Permission>;

export type Routes = typeof Routes;
